// src/features/dashboard/components/ManagerDashboard/ProductivityReport/ProductivityKpiCards.tsx

import {
  Users,
  UserCheck,
  TrendingUp,
  TrendingDown,
  Target,
} from 'lucide-react';

import {
  ProductivityKpiResponse,
} from '../../../types/Manager/productivityReport.types';

interface Props {
  data: ProductivityKpiResponse | null;
}

const ProductivityKPICards = ({
  data,
}: Props) => {

  if (!data) {
    return null;
  }

  const cards = [
    {
      title:
        'Span Avg. Productive Time',

      value:
        `${data.currentAvgProductivity}`,

      unit:
        '%',

      sub:
        data.productivityChange >= 0
          ? `▲ +${data.productivityChange}% vs last wk`
          : `▼ ${data.productivityChange}% vs last wk`,

      iconBg:
        'bg-[#E8F8F1]',

      iconColor:
        'text-[#27AE60]',

      icon:
        Target,

      subColor:
        'text-[#667085]',
    },

    {
      title:
        'Above Target (≥75%)',

      value:
        data.aboveTargetCount,

      unit:
        '',

      sub:
        `of ${data.totalAssociates} associates`,

      iconBg:
        'bg-[#EAF3FF]',

      iconColor:
        'text-[#2F80ED]',

      icon:
        UserCheck,

      subColor:
        'text-[#667085]',
    },

    {
      title:
        'Below Target (<75%)',

      value:
        data.belowTargetCount,

      unit:
        '',

      sub:
        'needs attention',

      iconBg:
        'bg-[#FFF2EA]',

      iconColor:
        'text-[#F26A21]',

      icon:
        Users,

      subColor:
        'text-[#667085]',
    },

    {
      title:
        'Top Team',

      value:
        data.topTeamProductivity,

      unit:
        '%',

      sub:
        data.topTeam,

      iconBg:
        'bg-[#F3ECFF]',

      iconColor:
        'text-[#8B5CF6]',

      icon:
        TrendingUp,

      subColor:
        'text-[#667085]',
    },

    {
      title:
        'Lowest Team',

      value:
        data.lowestTeamProductivity,

      unit:
        '%',

      sub:
        data.lowestTeam,

      iconBg:
        'bg-[#FDECEC]',

      iconColor:
        'text-[#EB5757]',

      icon:
        TrendingDown,

      subColor:
        'text-[#667085]',
    },
  ];

  return (

    <div
      className="
        grid
        w-full
        min-w-0

        grid-cols-1

        gap-3

        sm:grid-cols-2

        md:grid-cols-3

        lg:grid-cols-5

        /* ==========================================================
           110% ZOOM
        =========================================================== */

        [@media(max-width:1100px)]:gap-2.5

        /* ==========================================================
           125% - 150% ZOOM
        =========================================================== */

        [@media(max-width:1000px)]:gap-2

        /* Keep 5 cards in one row on compact desktop */
        [@media(min-width:900px)]:grid-cols-5
      "
    >

      {cards.map(
        (card) => {

          const Icon =
            card.icon;

          return (

            <div
              key={card.title}
              className="
                flex
                min-w-0
                w-full
                overflow-hidden

                rounded-[18px]

                border
                border-[#D8DCE3]

                bg-white

                h-[96px]

                px-3
                py-3

                transition-shadow
                duration-200

                hover:shadow-sm

                /* ==================================================
                   110% ZOOM
                =================================================== */

                [@media(max-width:1100px)]:h-[88px]
                [@media(max-width:1100px)]:rounded-[14px]
                [@media(max-width:1100px)]:px-2.5
                [@media(max-width:1100px)]:py-2.5

                /* ==================================================
                   125% - 150% ZOOM
                =================================================== */

                [@media(max-width:1000px)]:h-[80px]
                [@media(max-width:1000px)]:rounded-[12px]
                [@media(max-width:1000px)]:px-2
                [@media(max-width:1000px)]:py-2
              "
            >

              {/* ====================================================
                  CARD CONTENT
              ==================================================== */}

              <div
                className="
                  flex
                  min-w-0
                  w-full
                  items-start

                  gap-[10px]

                  [@media(max-width:1100px)]:gap-2

                  [@media(max-width:1000px)]:gap-1.5
                "
              >

                {/* ==================================================
                    ICON
                =================================================== */}

                <div
                  className={`
                    flex
                    h-[32px]
                    w-[32px]
                    shrink-0
                    items-center
                    justify-center
                    rounded-full

                    ${card.iconBg}

                    [@media(max-width:1100px)]:h-[28px]
                    [@media(max-width:1100px)]:w-[28px]

                    [@media(max-width:1000px)]:h-[25px]
                    [@media(max-width:1000px)]:w-[25px]
                  `}
                >

                  <Icon
                    size={16}
                    strokeWidth={2}
                    className={`
                      ${card.iconColor}

                      [@media(max-width:1100px)]:h-[14px]
                      [@media(max-width:1100px)]:w-[14px]

                      [@media(max-width:1000px)]:h-[13px]
                      [@media(max-width:1000px)]:w-[13px]
                    `}
                  />

                </div>


                {/* ==================================================
                    CONTENT
                =================================================== */}

                <div
                  className="
                    flex
                    min-w-0
                    flex-1
                    flex-col
                    overflow-hidden
                  "
                >

                  {/* =================================================
                      TITLE
                  ================================================== */}

                  <h4
                    className="
                      min-w-0
                      overflow-hidden

                      text-[11px]
                      font-semibold
                      leading-[14px]

                      text-[#111827]

                      whitespace-normal

                      line-clamp-2

                      [@media(max-width:1100px)]:text-[10px]
                      [@media(max-width:1100px)]:leading-[12px]

                      [@media(max-width:1000px)]:text-[9px]
                      [@media(max-width:1000px)]:leading-[11px]
                    "
                    title={card.title}
                  >
                    {card.title}
                  </h4>


                  {/* =================================================
                      VALUE
                  ================================================== */}

                  <div
                    className="
                      mt-[5px]

                      flex
                      min-w-0
                      items-baseline
                      gap-[2px]

                      [@media(max-width:1100px)]:mt-[3px]

                      [@media(max-width:1000px)]:mt-[2px]
                    "
                  >

                    <span
                      className="
                        min-w-0
                        overflow-hidden
                        text-ellipsis
                        whitespace-nowrap

                        text-[18px]
                        font-bold
                        leading-[20px]

                        text-[#111827]

                        [@media(max-width:1100px)]:text-[15px]
                        [@media(max-width:1100px)]:leading-[17px]

                        [@media(max-width:1000px)]:text-[13px]
                        [@media(max-width:1000px)]:leading-[15px]
                      "
                      title={`${card.value}${card.unit}`}
                    >
                      {card.value}
                    </span>


                    {/* =================================================
                        UNIT
                    ================================================== */}

                    {card.unit && (

                      <span
                        className="
                          shrink-0

                          text-[11px]
                          font-medium
                          leading-[14px]

                          text-[#667085]

                          [@media(max-width:1100px)]:text-[9px]
                          [@media(max-width:1100px)]:leading-[11px]

                          [@media(max-width:1000px)]:text-[8px]
                          [@media(max-width:1000px)]:leading-[10px]
                        "
                      >
                        {card.unit}
                      </span>

                    )}

                  </div>


                  {/* =================================================
                      SUBTITLE
                  ================================================== */}

                  <div
                    className={`
                      mt-[2px]

                      min-w-0
                      overflow-hidden

                      text-[9px]
                      font-medium
                      leading-[12px]

                      ${card.subColor}

                      whitespace-normal

                      line-clamp-2

                      [@media(max-width:1100px)]:text-[8px]
                      [@media(max-width:1100px)]:leading-[10px]

                      [@media(max-width:1000px)]:text-[7px]
                      [@media(max-width:1000px)]:leading-[9px]
                    `}
                    title={String(
                      card.sub,
                    )}
                  >
                    {card.sub}
                  </div>

                </div>

              </div>

            </div>
          );
        },
      )}

    </div>
  );
};

export default ProductivityKPICards;